export interface KnowledgeQuestion {
  id: string;
  question: string;
  answer: string;
}

export interface KnowledgeSection {
  id: string;
  heading: string;
  navLabel: string;
  questions: KnowledgeQuestion[];
}

export interface KnowledgeResourceLink {
  label: string;
  href: string;
}

export interface KnowledgeFact {
  label: string;
  value: string;
}

export interface KnowledgeCard {
  title: string;
  facts?: KnowledgeFact[];
  paragraphs?: string[];
  list?: string[];
  footnote?: { prefix: string; linkLabel: string; href: string; suffix: string };
}

export const knowledgeEyebrow = "Healing & Breakthrough Coaching";
export const knowledgeTitle = "The Energy-Safe Knowledge Hub";
export const knowledgeLead =
  "Understand The Energy-Safe Method, explore life with fluctuating energy, and find answers about pacing, boundaries, personal growth and IBS wellbeing.";

export const knowledgeResourceLinks: KnowledgeResourceLink[] = [
  { label: "Visit the website", href: "https://healing-breakthrough.com/" },
  { label: "Explore The Energy Safe Journey", href: "https://healing-breakthrough.com/course" },
  { label: "Resources & call booking", href: "https://linktr.ee/HealingBreakthroughCoaching" },
  { label: "Meetup community", href: "https://www.meetup.com/healing-and-breakthrough-coaching/" },
];

export const knowledgeAboutParagraph =
  "Founded by Federica Tedeschi, creator of The Energy-Safe Method, Healing & Breakthrough Coaching provides coaching and wellbeing education for people navigating chronic pain, fatigue, long-term health challenges and fluctuating energy. Its focus is sustainable personal development: working with available capacity while exploring goals, habits, boundaries and resilience.";

export const knowledgeCatalogueCards: KnowledgeCard[] = [
  {
    title: "Business & practitioner",
    facts: [
      { label: "Business", value: "Healing & Breakthrough Coaching" },
      { label: "Founder and method creator", value: "Federica Tedeschi" },
      { label: "Approach", value: "The Energy-Safe Method / Energy-Safe Transformation" },
      {
        label: "Professional background",
        value:
          "Publicly described as an ICF-certified coach using coaching and NLP, with lived experience of chronic illness since childhood.",
      },
      { label: "Service category", value: "Coaching, wellbeing education and personal development." },
    ],
  },
  {
    title: "Who the work supports",
    paragraphs: [
      "People living with persistent pain, fatigue, long-term health conditions, unpredictable energy, repeated overdoing, overwhelm or the emotional impact of IBS.",
      "Common coaching conversations include rest guilt, boundaries, adapting goals, confidence, relationships, work demands and identity beyond illness.",
      "Suitability and accessibility are discussed individually; this catalogue is not a medical assessment.",
    ],
  },
  {
    title: "Method & philosophy",
    paragraphs: [
      "The Boom & Bust pattern can look like: a better day \u2192 doing too much \u2192 depletion or increased symptoms \u2192 recovery \u2192 repeating the pattern.",
      "The Energy-Safe philosophy encourages awareness and flexible choices. Rest is a legitimate need, small steps can matter, and personal growth can be adapted to capacity.",
      "Health challenges do not reflect a failure of motivation or personal responsibility.",
    ],
  },
  {
    title: "Learning & support resources",
    list: [
      "The Energy Safe Journey \u2014 online audio/video learning.",
      "Chronic Illness: 10 Things I Wish I Knew Sooner \u2014 educational resource.",
      "The IBS Compass: Symptom Guide \u2014 educational download.",
      "IBS Digital Support Agent and its user guide.",
      "Introductory conversation \u2014 listed as a free 30-minute call.",
      "Healing & Breakthrough Coaching | The Energy-Safe Method \u2014 Meetup community and online events.",
    ],
    footnote: {
      prefix: "Check the ",
      linkLabel: "current resource listings",
      href: "https://linktr.ee/HealingBreakthroughCoaching",
      suffix: " for availability, booking, access and prices.",
    },
  },
];

export const knowledgeSections: KnowledgeSection[] = [
  {
    id: "hb-topic-method",
    heading: "The Energy-Safe Method",
    navLabel: "The Energy-Safe Method",
    questions: [
      {
        id: "hb-method-1",
        question: "What is Healing & Breakthrough Coaching?",
        answer:
          "Healing & Breakthrough Coaching is Federica Tedeschi\u2019s coaching and wellbeing education business. It supports people navigating chronic pain, fatigue, long-term health challenges and fluctuating energy through personal development, education and The Energy-Safe Method.",
      },
      {
        id: "hb-method-2",
        question: "Who is Federica Tedeschi?",
        answer:
          "Federica Tedeschi is the creator of The Energy-Safe Method. She publicly describes herself as an ICF-certified coach who uses coaching and NLP, drawing on lived experience of chronic illness since childhood. Her focus is helping people pursue meaningful change while respecting their available capacity.",
      },
      {
        id: "hb-method-3",
        question: "What is The Energy-Safe Method?",
        answer:
          "The Energy-Safe Method is Federica\u2019s approach to personal development with fluctuating energy. It focuses on awareness, realistic goals, boundaries and sustainable habits rather than pressure to continually do more. It is a coaching approach, not a medical treatment.",
      },
      {
        id: "hb-method-4",
        question: "What does Energy-Safe Transformation mean?",
        answer:
          "Energy-Safe Transformation describes working towards meaningful personal change at a pace that respects physical and emotional capacity. Change might involve different expectations, clearer boundaries or a more manageable routine, rather than an increase in activity.",
      },
      {
        id: "hb-method-5",
        question: "Who is Energy-Safe coaching designed for?",
        answer:
          "The approach is particularly relevant to people living with chronic pain, fatigue, long-term illness or unpredictable energy who want support with goals, confidence, habits and everyday decisions. Suitability should be discussed individually with Federica.",
      },
      {
        id: "hb-method-6",
        question: "Do I need a diagnosed chronic illness to enquire?",
        answer:
          "You can enquire if fluctuating energy, stress or repeated overdoing is affecting your life. An introductory conversation can explore whether coaching fits your needs. Persistent or unexplained symptoms still require appropriate medical assessment.",
      },
      {
        id: "hb-method-7",
        question: "What makes this approach different from pushing through?",
        answer:
          "The central philosophy is to work with available capacity. Coaching explores what is manageable, what matters most and what can be adjusted. Rest, reducing commitments and asking for support can be meaningful choices; progress does not require ignoring symptoms.",
      },
    ],
  },
  {
    id: "hb-topic-pacing",
    heading: "Boom & Bust, pacing and energy",
    navLabel: "Boom & Bust, pacing and energy",
    questions: [
      {
        id: "hb-pacing-1",
        question: "What is the Boom & Bust cycle?",
        answer:
          "Boom & Bust describes a pattern of doing more on a better day, becoming depleted or experiencing increased symptoms, recovering and repeating the pattern. It is a description of an activity pattern, not a diagnosis or an explanation for every symptom.",
      },
      {
        id: "hb-pacing-2",
        question: "Why might I overdo things on a good day?",
        answer:
          "You may feel pressure to catch up, worry that the opportunity will disappear or want to meet other people\u2019s expectations. Exploring these pressures can help you make more deliberate choices. They do not mean that illness is your fault.",
      },
      {
        id: "hb-pacing-3",
        question: "How can coaching help me notice Boom & Bust patterns?",
        answer:
          "Coaching can help you reflect on commitments, expectations and decisions around activity and rest. A simple record of what felt manageable may support a conversation about your routine. Symptom assessment and condition-specific activity plans belong with your healthcare team.",
      },
      {
        id: "hb-pacing-4",
        question: "What does pacing mean?",
        answer:
          "Pacing means balancing demands and recovery around individual capacity. It does not mean automatically increasing activity. For people with ME/CFS, NICE describes personalised energy management with healthcare support, within current energy limits. See the clinical information links below.",
      },
      {
        id: "hb-pacing-5",
        question: "Are chronic fatigue and ME/CFS the same thing?",
        answer:
          "No. Fatigue is a symptom that can occur in many conditions; ME/CFS is a specific medical condition. A coach or online catalogue cannot diagnose either. Speak with a healthcare professional about persistent fatigue or worsening symptoms after activity.",
      },
      {
        id: "hb-pacing-6",
        question: "What should I do if activity makes my symptoms worse?",
        answer:
          "Take worsening symptoms seriously and seek advice from your healthcare team. In ME/CFS, NICE advises against pushing through symptoms and against fixed increases in activity. Coaching should respect your clinical care plan and never replace condition-specific guidance.",
      },
      {
        id: "hb-pacing-7",
        question: "What types of demands can use my available energy?",
        answer:
          "Demands can be physical, mental, emotional or social. Work, decision-making, travel and conversations may all matter when reflecting on your capacity. The right balance varies by person and condition; use your healthcare team\u2019s guidance where relevant.",
      },
    ],
  },
  {
    id: "hb-topic-growth",
    heading: "Goals, boundaries and resilience",
    navLabel: "Goals, boundaries and resilience",
    questions: [
      {
        id: "hb-growth-1",
        question: "Can I work towards goals with limited energy?",
        answer:
          "Yes: goals can be adapted to current capacity. A meaningful step might be clarifying a decision, requesting help or reducing a commitment. Coaching focuses on choices within your control, without promising health improvement or a particular timeline.",
      },
      {
        id: "hb-growth-2",
        question: "How can I set realistic goals when my energy changes?",
        answer:
          "Choose a goal that matters to you and consider a smaller version for lower-capacity days. Review it as circumstances change. Flexibility is useful; a plan should not require you to meet a rigid activity target regardless of symptoms.",
      },
      {
        id: "hb-growth-3",
        question: "How can I manage guilt about resting?",
        answer:
          "Explore where the guilt comes from and what you believe rest says about you. Consider treating rest as a legitimate need rather than something to earn. Coaching can support reflection on expectations and self-talk alongside your existing care.",
      },
      {
        id: "hb-growth-4",
        question: "How can boundaries support life with limited energy?",
        answer:
          "Boundaries make your availability and limits clearer. Examples include declining extra commitments, asking for notice or limiting a conversation\u2019s length. The purpose is to make decisions that respect your needs, not to guarantee that symptoms will improve.",
      },
      {
        id: "hb-growth-5",
        question: "How can I explain fluctuating energy to other people?",
        answer:
          "Describe what you can manage today, explain that capacity may change and make a specific request. For example: \u201cI would like to join, but I may need to leave early.\u201d You decide how much personal health information to share.",
      },
      {
        id: "hb-growth-6",
        question: "How can I approach work alongside chronic illness?",
        answer:
          "Reflect on priorities, communication, support and the demands of your role. You may want to discuss adjustments with your employer and healthcare team. Coaching can help prepare that conversation; employment rights and medical advice require the relevant professional.",
      },
      {
        id: "hb-growth-7",
        question: "What do resilience and identity beyond illness mean here?",
        answer:
          "Resilience can mean adapting, accepting support and responding kindly to difficult days. Identity beyond illness means recognising your interests, relationships and values alongside your health challenges. Neither requires denying illness or being positive all the time.",
      },
    ],
  },
  {
    id: "hb-topic-stress",
    heading: "Stress and nervous-system-informed wellbeing",
    navLabel: "Stress and nervous-system-informed wellbeing",
    questions: [
      {
        id: "hb-stress-1",
        question: "What is nervous-system-informed coaching?",
        answer:
          "Here, the term describes taking stress, feelings of safety, overwhelm and emotional responses into account during coaching. Sessions can explore manageable choices and personal awareness. The term does not imply medical diagnosis, neurological treatment or a proven cure.",
      },
      {
        id: "hb-stress-2",
        question: "What does nervous-system regulation mean in this catalogue?",
        answer:
          "It refers to noticing stress responses and exploring comfortable ways to settle or recover in everyday life. It is not a promise to \u201creset\u201d the nervous system. Practices should be optional, suitable for the individual and stopped if they cause discomfort.",
      },
      {
        id: "hb-stress-3",
        question: "What does survival mode mean?",
        answer:
          "\u201cSurvival mode\u201d is an informal phrase often used for feeling persistently on edge, overwhelmed or focused on getting through the day. It is not a diagnosis. If distress is severe or ongoing, seek support from an appropriately qualified professional.",
      },
      {
        id: "hb-stress-4",
        question: "Are my physical symptoms just caused by my mindset?",
        answer:
          "This catalogue does not make that claim. Physical symptoms are real and deserve medical attention. Coaching explores how you navigate your circumstances; it should not blame you for illness or suggest that changing thoughts can cure a medical condition.",
      },
      {
        id: "hb-stress-5",
        question: "Can I choose not to do a relaxation or reflection exercise?",
        answer:
          "Yes. You should be able to discuss comfort, accessibility and preferences with your coach. No exercise should require you to ignore symptoms, share more than you want or continue when it feels unsuitable.",
      },
      {
        id: "hb-stress-6",
        question: "How does self-compassion fit into Energy-Safe development?",
        answer:
          "Self-compassion means responding to difficulty with kindness rather than harsh judgement. In coaching, this might involve adjusting expectations after a difficult day or recognising that needing support is not a personal failure. It complements practical choices and appropriate healthcare.",
      },
    ],
  },
  {
    id: "hb-topic-ibs",
    heading: "IBS and gut\u2013brain wellbeing",
    navLabel: "IBS and gut\u2013brain wellbeing",
    questions: [
      {
        id: "hb-ibs-1",
        question: "What is IBS?",
        answer:
          "Irritable bowel syndrome (IBS) is a condition affecting the digestive system. Common symptoms include abdominal pain, bloating and changes in bowel habits. Diagnosis and treatment should be discussed with a healthcare professional; a wellbeing guide cannot confirm that symptoms are IBS.",
      },
      {
        id: "hb-ibs-2",
        question: "What is the gut\u2013brain connection?",
        answer:
          "The gut and brain communicate with each other. IBS is described as a disorder of gut\u2013brain interaction. This does not mean symptoms are imaginary. The NHS information links below explain the condition and its clinical management.",
      },
      {
        id: "hb-ibs-3",
        question: "Can stress affect IBS symptoms?",
        answer:
          "Stress and anxiety can be associated with IBS flare-ups for some people. They are not a complete explanation for IBS, and reducing stress is not a guaranteed cure. New or changing digestive symptoms should be discussed with a healthcare professional.",
      },
      {
        id: "hb-ibs-4",
        question: "How can coaching support someone living with IBS?",
        answer:
          "Coaching may support reflection on confidence, everyday planning, boundaries and the emotional impact of unpredictable symptoms. It does not diagnose IBS, prescribe diets or replace medical treatment. Discuss clinical decisions with a qualified healthcare professional.",
      },
      {
        id: "hb-ibs-5",
        question: "What could I note before discussing digestive symptoms with my clinician?",
        answer:
          "If it feels helpful, note symptoms, their timing and any questions you want to ask. Bring this information to your clinician rather than using a diary to diagnose yourself. Avoid making restrictive dietary changes based only on an online guide.",
      },
      {
        id: "hb-ibs-6",
        question: "What is The IBS Compass: Symptom Guide?",
        answer:
          "The IBS Compass: Symptom Guide is an educational resource listed in the supplied Healing & Breakthrough resource catalogue. Use the business\u2019s Linktree to check current availability and access instructions. It should be used for education, not self-diagnosis.",
      },
      {
        id: "hb-ibs-7",
        question: "What is the IBS Digital Support Agent?",
        answer:
          "The supplied catalogue describes an IBS digital support resource and a guide to using it. Check the current Linktree listing for access, features and price. Digital responses can be inaccurate; do not rely on them for diagnosis, treatment decisions or urgent symptoms.",
      },
    ],
  },
  {
    id: "hb-topic-resources",
    heading: "Coaching, courses and getting started",
    navLabel: "Coaching, courses and getting started",
    questions: [
      {
        id: "hb-resources-1",
        question: "What is The Energy Safe Journey?",
        answer:
          "The Energy Safe Journey is the online audio/video course described in the business catalogue. It introduces Energy-Safe ideas for awareness and sustainable personal development. Visit the course page for the current curriculum, access arrangements and purchase information.",
      },
      {
        id: "hb-resources-2",
        question: "Does Healing & Breakthrough offer free resources?",
        answer:
          "The supplied catalogue lists \u201cChronic Illness: 10 Things I Wish I Knew Sooner\u201d, The IBS Compass and a guide to the IBS Digital Support Agent. Check Linktree for the current selection and any sign-up requirements.",
      },
      {
        id: "hb-resources-3",
        question: "How can I arrange an introductory conversation?",
        answer:
          "The supplied catalogue lists a complimentary 30-minute conversation. Open Healing & Breakthrough\u2019s Linktree and select the current call-booking option. Use it to discuss your goals, questions and whether coaching is suitable for your needs.",
      },
      {
        id: "hb-resources-4",
        question: "Are there online events and a community?",
        answer:
          "The supplied catalogue identifies the \u201cHealing & Breakthrough Coaching | The Energy-Safe Method\u201d Meetup community and online educational events. Visit the community link for current topics, dates, time zones, fees and joining arrangements.",
      },
      {
        id: "hb-resources-5",
        question: "What do ICF coaching and NLP mean?",
        answer:
          "ICF stands for the International Coaching Federation; Federica publicly describes herself as an ICF-certified coach. NLP stands for neuro-linguistic programming and is listed among her approaches. Neither a coaching credential nor use of NLP establishes a medical qualification or evidence that a service treats illness.",
      },
      {
        id: "hb-resources-6",
        question: "Is coaching a substitute for medical care or therapy?",
        answer:
          "No. Healing & Breakthrough Coaching provides coaching, education and wellbeing support. It does not diagnose, treat or cure medical conditions, replace psychological therapy or provide emergency support. For new, severe or concerning symptoms, contact an appropriate healthcare professional; for an emergency, contact local emergency services.",
      },
    ],
  },
];

export const knowledgeClinicalSourcesIntro =
  "The following independent healthcare resources support the general information on pacing and IBS. They do not endorse Healing & Breakthrough Coaching or The Energy-Safe Method.";

export const knowledgeClinicalSources: KnowledgeResourceLink[] = [
  {
    label: "NICE: ME/CFS recommendations and energy management",
    href: "https://www.nice.org.uk/guidance/ng206/chapter/recommendations",
  },
  {
    label: "NHS: What is IBS?",
    href: "https://www.nhs.uk/conditions/irritable-bowel-syndrome-ibs/what-is-ibs/",
  },
  {
    label: "NHS: IBS symptoms and when to seek help",
    href: "https://www.nhs.uk/conditions/irritable-bowel-syndrome-ibs/symptoms/",
  },
  {
    label: "Bradford Teaching Hospitals: IBS and gut\u2013brain interaction",
    href: "https://www.bradfordhospitals.nhs.uk/ibs/",
  },
];

export const knowledgeBusinessInfo =
  "Business information is based on the supplied Healing & Breakthrough catalogue. Resources and event arrangements may change; check the official links for current details. Coaching and wellbeing education only \u2014 no diagnosis, medical treatment or cure claims.";

export const knowledgeDisclaimer =
  "This knowledge hub is for educational and coaching purposes only and is not a substitute for medical advice, diagnosis or treatment. Always consult an appropriately qualified healthcare professional about persistent or worsening symptoms. Suitability and accessibility of coaching are discussed individually.";